import { useState, useEffect } from 'react';
import { collection, query, where, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useAuth } from '../components/auth/AuthContext';

/**
 * React hook to fetch and monitor subscription information from Stripe Firebase Extension
 * 
 * @returns {Object} Subscription data including:
 *   - subscription: The active subscription object or null
 *   - loading: Boolean indicating if data is being fetched
 *   - error: Error message if any
 *   - isActive: Boolean indicating if user has an active subscription
 *   - isRecurring: Boolean indicating if subscription is recurring
 *   - cancelAtPeriodEnd: Boolean indicating if subscription is set to cancel at period end
 *   - startDate: Subscription start date (Date object)
 *   - endDate: Subscription end date (Date object)
 *   - currentPeriodEnd: Current billing period end date (Date object)
 *   - productId: Stripe product ID
 *   - priceId: Stripe price ID
 *   - status: Subscription status (active, canceled, past_due, etc.)
 */
export const useSubscription = () => {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    let unsubscribe = null;

    const fetchSubscription = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('[useSubscription] Starting fetch for user:', currentUser.uid);

        // The Stripe Firebase Extension stores customer data
        // with Firebase UID as the document ID in the 'stripe_customers' collection
        const customerDocRef = doc(db, 'stripe_customers', currentUser.uid);
        console.log('[useSubscription] Checking customer document at:', `stripe_customers/${currentUser.uid}`);
        
        const customerDoc = await getDoc(customerDocRef);

        console.log('[useSubscription] Customer document exists:', customerDoc.exists());
        
        if (!customerDoc.exists()) {
          // No customer record found - user has no subscription
          console.log('[useSubscription] No customer document found - user has no subscription');
          setSubscription(null);
          setLoading(false);
          return;
        }

        const customerData = customerDoc.data();
        console.log('[useSubscription] Customer data:', customerData);
        
        // The Stripe Firebase Extension may store the customer ID in different fields
        // Check multiple possible field names
        const stripeCustomerId = customerData.id || 
                                 customerData.stripeCustomerId || 
                                 customerData.stripeId ||  // Extension uses this
                                 customerData.customerId;
        console.log('[useSubscription] Stripe customer ID:', stripeCustomerId);

        if (!stripeCustomerId) {
          console.log('[useSubscription] No Stripe customer ID found');
          setSubscription(null);
          setLoading(false);
          return;
        }

        // Query subscriptions subcollection for this customer
        // Note: We query ALL subscriptions first, then filter in code to ensure we catch them
        // The Stripe extension may not have set status immediately
        const subscriptionsRef = collection(db, 'stripe_customers', currentUser.uid, 'subscriptions');
        console.log('[useSubscription] Setting up listener for subscriptions at:', `stripe_customers/${currentUser.uid}/subscriptions`);
        
        // Set up real-time listener for ALL subscriptions (we'll filter in code)
        unsubscribe = onSnapshot(
          subscriptionsRef,
          (snapshot) => {
            console.log('[useSubscription] Subscription snapshot:', {
              size: snapshot.size,
              empty: snapshot.empty,
              docs: snapshot.docs.map(doc => {
                const data = doc.data();
                // Safely serialize data for logging (convert Firestore Timestamps)
                const safeData = {};
                Object.keys(data).forEach(key => {
                  const value = data[key];
                  if (value && typeof value === 'object' && value.toDate && typeof value.toDate === 'function') {
                    safeData[key] = `[Timestamp: ${value.toDate().toISOString()}]`;
                  } else if (value && typeof value === 'object' && value.toMillis && typeof value.toMillis === 'function') {
                    safeData[key] = `[Timestamp: ${new Date(value.toMillis()).toISOString()}]`;
                  } else {
                    safeData[key] = value;
                  }
                });
                return { id: doc.id, data: safeData };
              })
            });

            if (snapshot.empty) {
              console.log('[useSubscription] No subscriptions found in collection');
              setSubscription(null);
              setLoading(false);
              return;
            }

            // Get the most recent active subscription
            // Filter for active subscriptions in code (more reliable than Firestore query)
            let latestSubscription = null;
            let latestCreated = 0;

            snapshot.forEach((doc) => {
              const subData = doc.data();
              
              console.log('[useSubscription] Checking subscription:', {
                id: doc.id,
                status: subData.status,
                created: subData.created
              });

              // Filter for active, trialing, or past_due subscriptions
              // Also accept subscriptions without status (might be newly created)
              const validStatuses = ['active', 'trialing', 'past_due'];
              const hasValidStatus = !subData.status || validStatuses.includes(subData.status);
              
              if (!hasValidStatus) {
                console.log('[useSubscription] Skipping subscription with invalid status:', subData.status);
                return;
              }

              const created = subData.created?.toMillis?.() || 
                             (subData.created instanceof Date ? subData.created.getTime() : 0) ||
                             (typeof subData.created === 'number' ? subData.created : 0) ||
                             Date.now(); // Default to now if no created date

              if (created > latestCreated) {
                latestCreated = created;
                latestSubscription = {
                  id: doc.id,
                  ...subData,
                };
              }
            });

            console.log('[useSubscription] Selected subscription:', latestSubscription);
            setSubscription(latestSubscription);
            setLoading(false);
          },
          (err) => {
            console.error('[useSubscription] Error fetching subscription:', err);
            setError(err.message || 'Failed to fetch subscription');
            setLoading(false);
          }
        );
      } catch (err) {
        console.error('Error in fetchSubscription:', err);
        setError(err.message || 'Failed to fetch subscription');
        setLoading(false);
      }
    };

    fetchSubscription();

    // Cleanup listener on unmount or user change
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  // Helper function to convert Firestore timestamp to Date
  const toDate = (timestamp) => {
    if (!timestamp) return null;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    if (timestamp instanceof Date) {
      return timestamp;
    }
    if (typeof timestamp === 'number') {
      return new Date(timestamp * 1000); // Convert seconds to milliseconds
    }
    if (typeof timestamp === 'string') {
      return new Date(timestamp);
    }
    return null;
  };

  // Process subscription data
  // IMPORTANT: Don't spread the raw subscription object as it contains Firestore Timestamps
  // Instead, explicitly extract and convert only the fields we need
  const subscriptionData = subscription ? {
    id: subscription.id,
    // Convert Firestore timestamps to Date objects
    startDate: toDate(subscription.created),
    currentPeriodStart: toDate(subscription.current_period_start),
    currentPeriodEnd: toDate(subscription.current_period_end),
    endDate: toDate(subscription.cancel_at) || toDate(subscription.current_period_end),
    // Convenience properties
    isActive: subscription.status === 'active' || subscription.status === 'trialing',
    isRecurring: subscription.status === 'active' && !subscription.cancel_at_period_end,
    cancelAtPeriodEnd: subscription.cancel_at_period_end || false,
    productId: subscription.items?.data?.[0]?.price?.product?.id || subscription.product?.id || null,
    priceId: subscription.items?.data?.[0]?.price?.id || subscription.price?.id || null,
    status: subscription.status || null,
    // Only include primitive values from original subscription
    metadata: subscription.metadata || null,
  } : null;

  return {
    subscription: subscriptionData,
    loading,
    error,
    isActive: subscriptionData?.isActive || false,
    isRecurring: subscriptionData?.isRecurring || false,
    cancelAtPeriodEnd: subscriptionData?.cancelAtPeriodEnd || false,
    startDate: subscriptionData?.startDate || null,
    endDate: subscriptionData?.endDate || null,
    currentPeriodEnd: subscriptionData?.currentPeriodEnd || null,
    productId: subscriptionData?.productId || null,
    priceId: subscriptionData?.priceId || null,
    status: subscriptionData?.status || null,
  };
};

export default useSubscription;

